/**
 * EmojiShortcodePlugin.tsx
 *
 * Lexical plugin that provides emoji shortcode autocomplete.
 * When user types `:` followed by characters, shows matching emoji suggestions.
 *
 * Features:
 * - Trigger on `:` character
 * - Show dropdown after 2+ characters typed
 * - Search emojis by ID and keywords
 * - Keyboard navigation (arrow keys, Enter, Escape)
 * - Click to select
 * - Auto-replace `:shortcode:` with emoji
 * - Positioned near cursor in editor
 *
 * Usage:
 * ```tsx
 * <EmojiShortcodePlugin />
 * ```
 */

import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import {
  $getSelection,
  $isRangeSelection,
  $isTextNode,
  COMMAND_PRIORITY_LOW,
  KEY_ARROW_DOWN_COMMAND,
  KEY_ARROW_UP_COMMAND,
  KEY_ENTER_COMMAND,
  KEY_ESCAPE_COMMAND,
  KEY_TAB_COMMAND,
} from 'lexical';
import { useCallback, useEffect, useId, useRef, useState } from 'react';
import { searchEmojis as fullSearchEmojis } from '@/utils/emojiAutocomplete';
import { cn } from '@/utils/cn';

/**
 * Search result type
 */
interface EmojiSearchResult {
  id: string;
  name: string;
  native: string;
}

/**
 * Dropdown position type
 */
interface DropdownPosition {
  top: number;
  left: number;
}

/**
 * Maximum number of suggestions to show
 */
const MAX_SUGGESTIONS = 8;

/**
 * Minimum characters after : to trigger search
 */
const MIN_QUERY_LENGTH = 2;

/**
 * Debounce delay for search (ms)
 */
const SEARCH_DEBOUNCE = 100;

/**
 * Search emojis by query string
 */
function searchEmojis(query: string): EmojiSearchResult[] {
  const results = fullSearchEmojis(query, MAX_SUGGESTIONS);
  return results.map(r => ({
    id: r.shortcode,
    name: r.shortcode,
    native: r.native,
  }));
}

/**
 * Extract shortcode from text at cursor position
 * Returns the shortcode (without :) and its start position
 */
function extractShortcode(
  text: string,
  cursorOffset: number
): { shortcode: string; startIndex: number } | null {
  // Look backwards from cursor for `:` character
  let startIndex = -1;

  for (let i = cursorOffset - 1; i >= 0; i--) {
    const char = text[i];

    // Found the colon - this is our start
    if (char === ':') {
      startIndex = i;
      break;
    }

    // If we hit a space or another colon, no valid shortcode
    if (char === ' ' || char === '\n' || char === '\t') {
      return null;
    }
  }

  if (startIndex === -1) {
    return null;
  }

  // Extract the shortcode (text between : and cursor)
  const shortcode = text.substring(startIndex + 1, cursorOffset);

  // Check if shortcode ends with : (complete shortcode)
  // If it does, we'll handle auto-replacement elsewhere
  if (shortcode.endsWith(':')) {
    return null;
  }

  // Only allow alphanumeric and underscore
  if (!/^[a-zA-Z0-9_]*$/.test(shortcode)) {
    return null;
  }

  return { shortcode, startIndex };
}

/**
 * Get the DOM position of the cursor for dropdown positioning
 */
function getCursorPosition(): DropdownPosition | null {
  const selection = window.getSelection();
  if (!selection || selection.rangeCount === 0) {
    return null;
  }

  const range = selection.getRangeAt(0);
  const rect = range.getBoundingClientRect();

  if (rect.width === 0 && rect.height === 0) {
    // Fallback: get position from parent element
    const parent = range.startContainer.parentElement;
    if (parent) {
      const parentRect = parent.getBoundingClientRect();
      return {
        top: parentRect.bottom,
        left: parentRect.left,
      };
    }
    return null;
  }

  return {
    top: rect.bottom + 4,
    left: rect.left,
  };
}

/**
 * EmojiShortcodePlugin component
 */
export function EmojiShortcodePlugin(): JSX.Element | null {
  const [editor] = useLexicalComposerContext();

  const [isOpen, setIsOpen] = useState(false);
  const [results, setResults] = useState<EmojiSearchResult[]>([]);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [position, setPosition] = useState<DropdownPosition | null>(null);
  const [shortcodeInfo, setShortcodeInfo] = useState<{
    startIndex: number;
    nodeKey: string;
  } | null>(null);

  const dropdownRef = useRef<HTMLDivElement>(null);
  const searchTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  /**
   * Insert selected emoji and replace the shortcode
   */
  const insertEmoji = useCallback(
    (emoji: EmojiSearchResult) => {
      if (!shortcodeInfo) return;

      editor.update(() => {
        const selection = $getSelection();
        if (!$isRangeSelection(selection)) return;

        const anchor = selection.anchor;
        const node = anchor.getNode();

        if (!$isTextNode(node)) return;

        const text = node.getTextContent();
        const cursorOffset = anchor.offset;

        // Find the : that started this shortcode
        const startIndex = shortcodeInfo.startIndex;
        const endIndex = cursorOffset;

        // Create new text with emoji replacing :shortcode
        const beforeText = text.substring(0, startIndex);
        const afterText = text.substring(endIndex);
        const newText = beforeText + emoji.native + afterText;

        // Update the node text
        node.setTextContent(newText);

        // Move cursor after the emoji
        const newCursorPosition = startIndex + emoji.native.length;
        selection.anchor.set(node.getKey(), newCursorPosition, 'text');
        selection.focus.set(node.getKey(), newCursorPosition, 'text');
      });

      // Close dropdown
      setIsOpen(false);
      setResults([]);
      setShortcodeInfo(null);

      // Return focus to editor
      editor.focus();
    },
    [editor, shortcodeInfo]
  );

  /**
   * Close dropdown and reset state
   */
  const closeDropdown = useCallback(() => {
    setIsOpen(false);
    setResults([]);
    setSelectedIndex(0);
    setShortcodeInfo(null);
  }, []);

  /**
   * Handle keyboard navigation
   */
  useEffect(() => {
    if (!isOpen) return;

    const removeArrowDown = editor.registerCommand(
      KEY_ARROW_DOWN_COMMAND,
      (event) => {
        event?.preventDefault();
        setSelectedIndex((prev) => Math.min(prev + 1, results.length - 1));
        return true;
      },
      COMMAND_PRIORITY_LOW
    );

    const removeArrowUp = editor.registerCommand(
      KEY_ARROW_UP_COMMAND,
      (event) => {
        event?.preventDefault();
        setSelectedIndex((prev) => Math.max(prev - 1, 0));
        return true;
      },
      COMMAND_PRIORITY_LOW
    );

    const removeEnter = editor.registerCommand(
      KEY_ENTER_COMMAND,
      (event) => {
        if (results.length > 0 && selectedIndex < results.length) {
          event?.preventDefault();
          insertEmoji(results[selectedIndex]);
          return true;
        }
        return false;
      },
      COMMAND_PRIORITY_LOW
    );

    const removeTab = editor.registerCommand(
      KEY_TAB_COMMAND,
      (event) => {
        if (results.length > 0 && selectedIndex < results.length) {
          event?.preventDefault();
          insertEmoji(results[selectedIndex]);
          return true;
        }
        return false;
      },
      COMMAND_PRIORITY_LOW
    );

    const removeEscape = editor.registerCommand(
      KEY_ESCAPE_COMMAND,
      (event) => {
        event?.preventDefault();
        closeDropdown();
        return true;
      },
      COMMAND_PRIORITY_LOW
    );

    return () => {
      removeArrowDown();
      removeArrowUp();
      removeEnter();
      removeTab();
      removeEscape();
    };
  }, [editor, isOpen, results, selectedIndex, insertEmoji, closeDropdown]);

  /**
   * Listen for text changes to drive the autocomplete DROPDOWN only.
   *
   * This listener may ONLY call React `setState` (open/close/results/
   * position) -- it must NEVER call `editor.update()`. Emoji insertion only
   * ever happens on explicit user action (Enter/Tab/click) in `insertEmoji`,
   * which runs outside this listener and is therefore not part of the
   * "editor.update() from an update listener" anti-pattern.
   *
   * There is intentionally NO automatic conversion of a completed
   * `:shortcode:` -- a previous node-transform-based auto-convert was
   * removed because it could not be gated reliably: node transforms run
   * against an in-flux selection during content rebuilds (e.g. loading a
   * draft, pasting, or restoring), so a loaded caption that literally
   * contained `:fire:` could get silently rewritten to an emoji before the
   * user touched anything. The ONLY way an emoji gets inserted now is via
   * this dropdown: the user types a partial shortcode, sees a match, and
   * explicitly selects it (Enter/Tab/click) through `insertEmoji`.
   */
  useEffect(() => {
    const removeListener = editor.registerUpdateListener(({ editorState }) => {
      editorState.read(() => {
        const selection = $getSelection();
        if (!$isRangeSelection(selection) || !selection.isCollapsed()) {
          closeDropdown();
          return;
        }

        const anchor = selection.anchor;
        const node = anchor.getNode();

        if (!$isTextNode(node)) {
          closeDropdown();
          return;
        }

        const text = node.getTextContent();
        const cursorOffset = anchor.offset;

        // Extract partial shortcode for autocomplete
        const extracted = extractShortcode(text, cursorOffset);

        if (!extracted || extracted.shortcode.length < MIN_QUERY_LENGTH) {
          closeDropdown();
          return;
        }

        // Debounce search
        if (searchTimeoutRef.current) {
          clearTimeout(searchTimeoutRef.current);
        }

        searchTimeoutRef.current = setTimeout(() => {
          const searchResults = searchEmojis(extracted.shortcode);

          if (searchResults.length === 0) {
            closeDropdown();
            return;
          }

          // Get cursor position for dropdown
          const cursorPos = getCursorPosition();
          if (!cursorPos) {
            closeDropdown();
            return;
          }

          setResults(searchResults);
          setSelectedIndex(0);
          setPosition(cursorPos);
          setShortcodeInfo({
            startIndex: extracted.startIndex,
            nodeKey: node.getKey(),
          });
          setIsOpen(true);
        }, SEARCH_DEBOUNCE);
      });
    });

    return () => {
      removeListener();
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, [editor, closeDropdown]);

  /**
   * Reset selected index when results change
   */
  useEffect(() => {
    setSelectedIndex(0);
  }, [results]);

  /**
   * Scroll selected item into view
   */
  useEffect(() => {
    if (!dropdownRef.current || !isOpen) return;

    const selectedElement = dropdownRef.current.querySelector(
      `[data-index="${selectedIndex}"]`
    );
    if (selectedElement) {
      selectedElement.scrollIntoView({ block: 'nearest' });
    }
  }, [selectedIndex, isOpen]);

  const listVisible = isOpen && position !== null && results.length > 0;
  const listId = useId();
  const activeOptionId = listVisible ? `${listId}-opt-${selectedIndex}` : undefined;

  /**
   * While the list is visible, mark the editor's contenteditable as the owner of an open popup.
   * The surrounding Radix Modal decides in the capture phase (before Lexical's bubble-phase
   * KEY_ESCAPE_COMMAND runs) whether Esc belongs to an inner popup, and it detects that via these attributes
   * (see isInsideOpenPopup). Removed again when the list closes or the plugin unmounts.
   */
  useEffect(() => {
    if (!listVisible) return undefined;
    const root = editor.getRootElement();
    if (!root) return undefined;
    root.setAttribute('aria-expanded', 'true');
    root.setAttribute('aria-haspopup', 'listbox');
    root.setAttribute('aria-controls', listId);
    return () => {
      root.removeAttribute('aria-expanded');
      root.removeAttribute('aria-haspopup');
      root.removeAttribute('aria-controls');
      root.removeAttribute('aria-activedescendant');
    };
  }, [editor, listVisible, listId]);

  useEffect(() => {
    const root = editor.getRootElement();
    if (!root || !activeOptionId) return;
    root.setAttribute('aria-activedescendant', activeOptionId);
  }, [editor, activeOptionId]);

  // Don't render anything if dropdown is closed
  if (!listVisible || !position) {
    return null;
  }

  return (
    <div
      ref={dropdownRef}
      id={listId}
      className={cn(
        'fixed z-[100000]',
        'bg-white border border-gray-200 rounded-lg shadow-lg',
        'py-1 min-w-[200px] max-w-[280px]',
        'animate-in fade-in-0 zoom-in-95 duration-100'
      )}
      style={{
        top: position.top,
        left: position.left,
      }}
      role="listbox"
      aria-label="Emoji suggestions"
    >
      {results.map((emoji, index) => (
        <button
          key={emoji.id}
          id={`${listId}-opt-${index}`}
          data-index={index}
          tabIndex={-1}
          type="button"
          role="option"
          aria-selected={index === selectedIndex}
          className={cn(
            'w-full px-3 py-2 flex items-center gap-3 text-left',
            'transition-colors cursor-pointer',
            index === selectedIndex
              ? 'bg-primary-50 text-primary-900'
              : 'hover:bg-gray-50 text-gray-700'
          )}
          onMouseDown={(e) => { e.preventDefault(); insertEmoji(emoji); }}
          onMouseEnter={() => setSelectedIndex(index)}
        >
          <span className="text-xl" role="img" aria-label={emoji.name}>
            {emoji.native}
          </span>
          <span className="text-sm font-medium truncate">{emoji.id}</span>
        </button>
      ))}

      {/* Hint text */}
      <div className="px-3 py-1.5 text-xs text-gray-400 border-t border-gray-100 mt-1">
        {/* eslint-disable-next-line i18next/no-literal-string */}
        <kbd className="px-1 py-0.5 bg-gray-100 rounded text-[10px]">Enter</kbd>
        {/* eslint-disable-next-line i18next/no-literal-string */}
        {' '}to select,{' '}
        {/* eslint-disable-next-line i18next/no-literal-string */}
        <kbd className="px-1 py-0.5 bg-gray-100 rounded text-[10px]">Esc</kbd>
        {/* eslint-disable-next-line i18next/no-literal-string */}
        {' '}to close
      </div>
    </div>
  );
}

export default EmojiShortcodePlugin;
